import { InputTypes } from 'constants/enums';
import { IFormField } from 'models/app';
import React from 'react';
import Modal from 'components/UI/DialogModal';
import Form from 'components/UI/Form';
import { CircularProgress, Grid, Stack } from '@mui/material';
import MuiButton from 'components/UI/MuiButton';
import { IIndustry } from 'models/industry';
import { useTranslation } from 'react-i18next';
import { IWorkshopEvaluation } from 'models/workshopEvaluation';
import { IVendorAssessmentDefinition } from 'models/vendorAssessmentDefinition';
import { IAssessmentLevels } from 'models/assessmentLevels';
import { ICompany } from 'models/company';
import { ICertifyingBody } from 'models/certifyingBody';


interface Props {
  open: boolean;
  loading: boolean;
  vendorAssessmentDefinition: IVendorAssessmentDefinition;
  assessmentLevels: IAssessmentLevels[];
  companies: ICompany[];
  certifyingBodies: ICertifyingBody[];
  onClose: () => void;
  onSubmit: () => void;
  onChanges: (name: string, value: any) => void;
}

const EditVendorAssessmentDefinition: React.FC<Props> = ({ open, loading, vendorAssessmentDefinition, assessmentLevels, companies, certifyingBodies, onClose, onSubmit, onChanges }) => {
  const { t } = useTranslation()
  let assessmentLevelOptions = assessmentLevels.map((assessmentLevel) => {
    return {
      value: assessmentLevel.id,
      label: assessmentLevel.name
    };
  });

  let companiesOptions = [
    {
      value: '',
      label: t('T_global_no_company') || 'Global (No Company)'
    },
    ...companies.map((company) => {
      return {
        value: company.id,
        label: company.name
      };
    })
  ];

  let certifyingBodiesOptions = [
    {
      value: '',
      label: t('T_none') || 'None'
    },
    ...certifyingBodies.filter(cb => cb.isActive).map((certifyingBody) => {
      return {
        value: certifyingBody.id,
        label: certifyingBody.name
      };
    })
  ];

  let basicFields: IFormField[] = [
    {
      name: 'title',
      label: t('T_enter_title'),
      type: InputTypes.TEXT,
      defaultValue: vendorAssessmentDefinition.title,
      internalLabel: true,
      required: true
    },
    {
      name: 'titleAr',
      label: `${t('T_enter_title')} ${t('T_in_arabic')}`,
      type: InputTypes.TEXT,
      defaultValue: vendorAssessmentDefinition.titleAr,
      internalLabel: true
    },
    {
      name: 'assessmentLevel',
      label: t('T_assessment_level'),
      type: InputTypes.SELECT,
      options: assessmentLevelOptions,
      defaultValue: assessmentLevelOptions.find(x => x.value == vendorAssessmentDefinition.assessmentLevel),
      internalLabel: true,
      required: true
    },
    {
      name: 'companyId',
      label: `${t('T_company')} (${t('T_optional')})`,
      type: InputTypes.SELECT,
      options: companiesOptions,
      defaultValue: companiesOptions.find(x => x.value == (vendorAssessmentDefinition.companyId || '')),
      internalLabel: true,
      required: false
    },
    {
      name: 'certifyingBodyId',
      label: `${t('T_certifying_body')} (${t('T_optional')})`,
      type: InputTypes.SELECT,
      options: certifyingBodiesOptions,
      defaultValue: certifyingBodiesOptions.find(x => x.value == (vendorAssessmentDefinition.certifyingBodyId || '')),
      internalLabel: true,
      required: false
    }
  ];



  return (<>

    <Modal
      open={open}
      onClose={onClose}
      title={t('T_edit_assessment')}
      maxWidth="lg"
      children={<Stack spacing={2} p={2} >
        <Form fields={basicFields} onChange={onChanges} />
      </Stack>}
      actions={
        <Grid container alignItems={'center'} justifyContent={'space-between'} px={1} >
          <MuiButton variant="contained"
            sx={{ bgcolor: 'warning.main', color: 'white' }}
            size='large' onClick={onClose}>
            {t('T_cancel')}
          </MuiButton>
          <MuiButton variant="contained"
            sx={{ bgcolor: 'primary.main', color: 'white' }}
            disabled={loading}
            size='large' onClick={onSubmit}>
            {t('T_edit')}
            <CircularProgress
              size={10}
              sx={{ mx: 2, display: loading ? "" : "none" }}
            />
          </MuiButton>

        </Grid>
      }
    />
  </>
  );
};

export default EditVendorAssessmentDefinition;
